import { isEthereumWallet } from "@dynamic-labs/ethereum";
import { useDynamicContext } from "@dynamic-labs/sdk-react-core";
import { evaluate, toast } from "@kyan/ui";
import { useState } from "react";
import { Address, formatUnits, parseUnits } from "viem";

import { clearingHouseProxy } from "#/config/web3/contractAddresses";
import { tokenAddresses, tokens } from "#/config/web3/tokens";
import { useDepositFeatureFlag } from "#/hooks/featureFlags/useDepositFeatureFlag";
import { useApproval } from "#/hooks/useApproval";
import { useChainId } from "#/hooks/useChainId";
import { usePortfolioToastAction } from "#/hooks/usePortfolioToastActions";
import { useRefetchAccountStateIfStale } from "#/hooks/useRefetchAccountStateIfStale";
import { useUsdcBalance } from "#/hooks/useUsdcBalance";
import { useWalletClient } from "#/hooks/useWalletClient";
import { waitForTransactionReceipt } from "#/services/blockchain/blockchain.service";
import { useAccountHistory } from "#/services/kyan/hooks/useAccountHistory";
import { useDeposit as useDepositService } from "#/services/kyan/hooks/useDeposit";
import { useNetDeposits } from "#/services/kyan/hooks/useNetDeposits";
import { useEquityModalPortfolio } from "#/store/app/equityModal/equityModalPortfolio";
import { useEoa } from "#/store/wallet/eoa/eoa";
import { approveToken } from "#/utils/approveToken";
import { formatCurrency } from "#/utils/valueFormatters";

import { useTxState } from "../../../../../EquityModal.hook";
import { TxStatus } from "../../../../../EquityModal.types";

export const useDeposit = ({
  amount,
  account,
}: {
  amount: string;
  account: string | undefined;
}) => {
  const { eoa } = useEoa();
  const { chainId } = useChainId();
  const { setTxState } = useTxState();
  const { usdcBalance, refetch: refetchBalance } = useUsdcBalance();
  const {
    hasReachedLimit,
    isLoading: isLoadingNetDeposits,
    remainingDepositCapacity,
  } = useNetDeposits();
  const { equityModalPortfolio } = useEquityModalPortfolio();
  const { refetch: refetchAccountHistory } = useAccountHistory();
  const { refetch } = useRefetchAccountStateIfStale();
  const { primaryWallet, network } = useDynamicContext();
  const { walletClient } = useWalletClient();

  const { getAction } = usePortfolioToastAction();
  const { isEnabled, minAmount } = useDepositFeatureFlag();

  const [isApproving, setIsApproving] = useState(false);

  /// @dev Placeholder: This single Sepolia addresses are the only valid ones for now.
  const depositTokenPair = {
    base: tokenAddresses[equityModalPortfolio][chainId],
    quote: tokenAddresses.USDC[chainId],
  };

  const {
    isAllowanceSufficient,
    refetch: refetchAllowance,
    isLoading: isApprovalLoading,
  } = useApproval({
    amount: parseUnits(amount || "0", 6),
    tokenAddress: depositTokenPair.quote,
    account: eoa,
    spender: clearingHouseProxy[chainId],
    chainId: chainId,
  });

  const { mutate: depositMutation, isPending: isDepositing } =
    useDepositService({
      onSuccess: (response) => {
        toast.success({
          title: "Deposit Successful",
          description: "Funds have been deposited to your account",
          action: getAction({
            closeModal: true,
            target: "history",
            label: "View in history",
          }),
        });
        refetchBalance();
        refetchAllowance();
        setTxState({
          status: TxStatus.SUCCESS,
          amount: Number(amount),
          toPortfolio: equityModalPortfolio,
          txHash: response.data.transactionHash,
        });
        refetchAccountHistory();
        refetch?.();
      },
      onError: (error) => {
        toast.error({ title: "Deposit Failed", description: error.message });
        setTxState({ status: TxStatus.ERROR });
      },
      onPendingDeposit: (signedDeposit) => {
        setTxState({ status: TxStatus.PENDING, amount: signedDeposit.amount });
      },
    });

  const isInvalidNetwork = Number(network) !== chainId;
  const isAmountValid = !isNaN(Number(amount)) && Number(amount) > 0;
  const hasInsufficientBalance =
    Number(amount) > Number(formatUnits(usdcBalance, tokens.USDC.decimals));

  const ctaLabel = evaluate({
    "Enter amount": !isAmountValid,
    "Deposit limit reached": hasReachedLimit,
    "Approve token (1/2)": !isAllowanceSufficient,
    "Sign to Deposit (2/2)": isAllowanceSufficient,
  });

  const errorMessage = evaluate({
    [`Minimum deposit amount is ${minAmount} USDC`]:
      isAmountValid && Number(amount) < minAmount,
    "Insufficient balance": hasInsufficientBalance,
    [`Deposit limit reached. Remaining capacity: ${formatCurrency(remainingDepositCapacity)}`]:
      hasReachedLimit,
  });

  /// @dev testing EoA accounts only for now:
  const handleDeposit = async () => {
    //Switch user network to kyan
    if (!primaryWallet || !isEthereumWallet(primaryWallet)) {
      return;
    }
    if (isInvalidNetwork) {
      await primaryWallet.switchNetwork(chainId);
      return;
    }

    if (!walletClient) {
      return walletClient;
    }

    if (!isAllowanceSufficient) {
      try {
        setIsApproving(true);
        const hash = await approveToken({
          amount: parseUnits(amount || "0", 6),
          spender: clearingHouseProxy[chainId],
          tokenAddress: depositTokenPair.quote,
          walletClient,
        });

        await waitForTransactionReceipt(
          {
            hash,
            confirmations: 2,
          },
          walletClient?.chain?.id,
        );

        await refetchAllowance();
      } catch (error) {
        return toast.error({
          title: "Error approving deposit",
        });
      } finally {
        setIsApproving(false);
      }
    } else {
      depositMutation({
        to: account as Address,
        from: eoa as Address,
        amount: Number(amount),
        pair: depositTokenPair,
      });
    }
  };

  const isLoading = isApproving || isDepositing || isApprovalLoading;
  const isFormValid =
    isAmountValid &&
    Number(amount) >= minAmount &&
    !hasInsufficientBalance &&
    !hasReachedLimit;
  return {
    handleDeposit,
    errorMessage,
    ctaLabel,
    isLoading: isLoading || isLoadingNetDeposits,
    isFormValid,
    isDisabled: !isFormValid || !isEnabled || hasReachedLimit,
    min: minAmount,
    max: Number(formatUnits(usdcBalance, 6)),
  };
};
